import { Component, Input } from '@angular/core';
import { getInitials } from './avatar.utils';

@Component({
  selector: 'app-avatar',
  standalone: true,
  imports: [],
  templateUrl: './avatar.component.html',
  styleUrl: './avatar.component.scss'
})
export class AvatarComponent {
  // Matches the contacts table's existing look by default — call sites that need a bigger
  // avatar (e.g. the menu footer) pass a larger size explicitly.
  @Input() size = 28;

  // Circle fill and initials colour. They default to the person pair rather than being
  // optional, so a call site with no contact type to key off (the menu footer's logged-in
  // user) still gets a legible pairing. Always override both together: a light background
  // left with the dark default initials, or the reverse, renders the initials invisible.
  @Input() color = 'var(--sgl-avatar-person)';
  @Input() textColor = 'var(--sgl-avatar-person-text)';

  // Pins the initials size exactly, for call sites following a design spec that doesn't track
  // the default ratio (e.g. the contact page's 46px person avatar wants 15px initials, not the
  // ~18px the ratio below would give it). Leave unset to keep the proportional default.
  @Input() fontSizeOverride: number | null = null;

  // Renders a small icon badge instead of the initials/photo (e.g. a department's transport-mode
  // icon) — takes precedence over both when set.
  @Input() iconUrl: string | null = null;

  initials = '';
  photoLoaded = false;
  photoFailed = false;

  private _photoUrl: string | null = null;

  get fontSize(): number {
    if (this.fontSizeOverride !== null) {
      return this.fontSizeOverride;
    }
    // Keeps initials sized proportionally to the circle at any `size` (28px circle : 11px
    // initials is the ratio this was originally designed at).
    return Math.round(this.size * (11 / 28));
  }

  @Input() set displayName(value: string) {
    this.initials = getInitials(value);
  }

  // Reset load/error state on every set, not just when the value changes — MatRecycleRows
  // reuses this component instance across virtual-scroll rows, so without this a recycled
  // row would briefly show the previous row's photo (or its failed state) until the new
  // <img> fires its own (load)/(error).
  @Input() set photoUrl(value: string | null) {
    this._photoUrl = value;
    this.photoLoaded = false;
    this.photoFailed = false;
  }

  get photoUrl(): string | null {
    return this._photoUrl;
  }
}
